
import { SearchOptions, SearchResponse } from '@algolia/client-search';
import { CATEGORIES, COLORS, ROOM_TYPES,STYLES } from '@data';
import algolia from '@integrations/Algolia';
import Sentry from '@integrations/Sentry';
import geocode from '@utils/geocode';
import getPlatform from '@utils/getPlatform';
import makeFacetFilters from '@utils/makeFacetFilters';
import makeIndexName from '@utils/makeIndexName';
import makeNumericFilter from '@utils/makeNumericFilter';
import { useRouter } from 'next/router';
import qs, { ParsedUrlQuery } from 'querystring';
import { useEffect, useState } from 'react';

import useDebounceEffect from './useDebounceEffect';

type UseFacetSearchOptions<T = guesthouse.Product> = {
  query: ParsedUrlQuery;
  initResults: SearchResponse<T>;
  hitsPerPage?: number;
  extraFacetFilters?: { [key: string] : string | string[] };
  extraNumericFilters?: string | readonly string[] | readonly (readonly string[])[];
  filters?: string;
  searchOptions?: Partial<SearchOptions>;
  page?: number;
  sortOption?: any;
  syncUrl?: boolean;
  indexOptions?: {
    init: string;
    offer_price_desc: string;
    offer_price_asc: string;
  }
}

const defaultOptions : Partial<UseFacetSearchOptions> = {
  hitsPerPage: 60,
  extraFacetFilters: {},
  extraNumericFilters: [],
  syncUrl: true,
  indexOptions: {
    init: 'products_in_stock_desc',
    offer_price_desc: 'products_offer_price_desc',
    offer_price_asc: 'products_offer_price_asc'
  }
};

const categories2 = [];

CATEGORIES.map(category => {
  category.subcategories && category.subcategories.map(category2 => {
    categories2.push(category2);
  });
});

export default function useFacetSearch<T = guesthouse.Product>(o : UseFacetSearchOptions<T>) {
  const options = { ...defaultOptions, ...o };
  const { indexOptions } = options;

  let index = algolia.initIndex(makeIndexName(indexOptions.init));

  if (indexOptions[options.sortOption]) {
    index = algolia.initIndex(makeIndexName(indexOptions[options.sortOption]));
  }

  let initQuery = '';

  if (options.query?.query) {
    if (Array.isArray(options.query.query && options.query.query.length > 1)) {
      initQuery = options.query.query[0];
    } else if (typeof options.query.query === 'string') {
      initQuery = options.query.query;
    }
  }

  const [results, setResults] = useState<SearchResponse<T>>(options.initResults);
  const [page, setPage] = useState<number>(0);
  const [query, setQuery] = useState<string>(initQuery);
  const [color, setColor] = useState<string | string[]>(options.query.color);
  const [style, setStyle] = useState<string | string[]>(options.query.style);
  const [room_types, setroom_types] = useState<string | string[]>(options.query.room_types);
  const [category, setCategory] = useState<string | string[]>(options.query.category);
  const [category2, setCategory2] = useState<string | string[]>(options.query.category2);
  const [price, setPrice] = useState<string | string[]>(options.query.price);
  const [geoSearch, setGeoSearch] = useState(false);
  const [geoSearchAddress, setGeoSearchAddress] = useState('');
  const [geoSearchDistance, setGeoSearchDistance] = useState(25);
  const [geoSearchLatLng, setGeoSearchLatLng] = useState<guesthouse.LatLng>();
  const router = useRouter();

  useDebounceEffect(() => {
    if (geoSearchAddress) {
      geocode({ address: geoSearchAddress })
        .then(response => {
          if (response.results) {
            const [first] = response.results;
            const geo = first.geometry.location;

            setGeoSearchLatLng(geo);
          }
        })
        .catch((e) => {
          Sentry.captureException(e);
        });
    } 
  }, 400, [geoSearch, geoSearchAddress, geoSearchDistance]);

  const doSearch = () => {
    const obj = {
      query,
      color,
      style,
      category,
      category2,
      room_types,
      price,
      page
    };

    for (const k in obj) {
      if (!obj[k] || (Array.isArray(obj[k]) && !obj[k].length)) {
        delete obj[k];
      }
    }

    const search = qs.stringify(obj);
    const url = Object.keys(obj).length ? `${router?.asPath.split('?')[0]}?${search}` : router?.asPath.split('?')[0];

    if (options?.syncUrl) {
      router?.push(url, undefined, { shallow: true });
    }

    const useGeoSearch = geoSearch && geoSearchLatLng && geoSearchDistance;

    let queryPage = 0;

    if (page > 0) {
      queryPage = page - 1;
    }

    const currentPlatform = getPlatform();
    const numericFilters = [
      ...makeNumericFilter({
        lowestOfferPrice: price
      }),
    ].concat(options.extraNumericFilters.toString().split(',')).filter(Boolean);
    
    const searchOptions : SearchOptions = {
      page: queryPage,
      distinct: 1,
      facetFilters: makeFacetFilters({
        color,
        style,
        category,
        category2,
        room_types,
        published: 'true',
        inStock: 'true',
        ...options.extraFacetFilters,
      }),
      filters: options.filters,
      numericFilters,
      facets: [
        'color',
        'style',
        'category',
        'category2',
        'room_types'
      ],
      hitsPerPage: options.hitsPerPage,
      aroundLatLng: useGeoSearch ? `${geoSearchLatLng.lat}, ${geoSearchLatLng.lng}` : undefined,
      aroundRadius: useGeoSearch ? Math.floor(geoSearchDistance * 1.60934 * 1000) : undefined,
      analyticsTags: [currentPlatform],
      ...(options.searchOptions || {})
    };

    if (query) {
      try {
        window.analytics?.track('Products Searched', {
          query
        });
      } catch (e) {
        Sentry.captureException(e);
      }
    }

    index.search<T>(query, searchOptions)
      .then(setResults)
      .catch((e) => {
        Sentry.captureException(e);
      });
  };

  useEffect(doSearch, []);
  useDebounceEffect(doSearch, 400, [
    page,
    query,
    color,
    style,
    category,
    category2,
    price,
    room_types,
    geoSearch,
    geoSearchLatLng,
    geoSearchDistance,
    options.sortOption
  ]);

  const filters = {
    category: {
      id: 'category',
      title: 'Categories',
      options: CATEGORIES.filter(c => c.public),
      values: category,
      values2: category2,
      counts: results.facets?.category,
      counts2: results.facets?.category2,
      setter: setCategory,
      setter2: setCategory2
    },
    category2: {
      id: 'subcategory',
      title: 'Categories',
      options: CATEGORIES.filter(c => c.id === options.extraFacetFilters['category'])?.[0]?.subcategories.filter(sc => sc.public),
      values: category2,
      counts: results.facets?.category2,
      setter: setCategory2,
    },
    color: {
      id: 'color',
      title: 'Color',
      options: COLORS,
      values: color,
      counts: results.facets?.color,
      setter: setColor,
    },
    style: {
      id: 'style',
      title: 'Style',
      options: STYLES,
      values: style,
      counts: results.facets?.style,
      setter: setStyle,
    },
    price: {
      id: 'price',
      title: 'Price',
      values: Array.isArray(price) ? price.map(i => i.toUpperCase()) : price?.toUpperCase(),
      counts: results.facets?.price,
      setter: setPrice,
      options: [
        {
          id: '0 TO 25',
          title: 'Under $25',
        },
        {
          id: '25 TO 250',
          title: '$25 - $250',
        },
        {
          id: '250 TO 500',
          title: '$250 - $500',
        },
        {
          id: 'price > 500',
          title: 'Over $500',
        }
      ]
    },
    room_types: {
      id: 'room_types',
      title: 'Room type',
      options: ROOM_TYPES,
      values: room_types,
      counts: results.facets?.room_types,
      setter: setroom_types,
    },
  };

  return {
    results,
    filters,
    query,
    setQuery,
    page,
    setPage,
    color,
    style,
    category,
    category2,
    room_types,
    geoSearch,
    setGeoSearch,
    geoSearchAddress,
    setGeoSearchAddress,
    geoSearchDistance,
    setGeoSearchDistance,
    geoSearchLatLng,
    setGeoSearchLatLng,
  };
}

export type FacetSearchServerSideOptions = {
  query: ParsedUrlQuery;
  hitsPerPage?: number;
  extraFacetFilters?: { [key: string] : string | string[] };
  extraNumericFilters?: string | readonly string[] | readonly (readonly string[])[];
  searchOptions?: Partial<SearchOptions>;
  page?: number;
  sortOption?: string;
}

export async function facetSearchServerSide<T = guesthouse.Product>( o : FacetSearchServerSideOptions) {
  const options = { ...defaultOptions, ...o };
  const { indexOptions } = options;

  const { query, hitsPerPage, extraFacetFilters, searchOptions, sortOption } = options;

  let index = algolia.initIndex(makeIndexName(indexOptions.init));

  if (sortOption) {
    index = algolia.initIndex(makeIndexName(indexOptions[sortOption]));
  }

  let initQuery = '';
  let initPage = 0;

  if (query?.query) {
    if (Array.isArray(query.query && query.query.length > 1)) {
      initQuery = query.query[0];
    } else if (typeof query.query === 'string') {
      initQuery = query.query;
    }
  }

  if (Number(query?.page) > 0) {
    initPage = Number(query.page) - 1;
  }

  const currentPlatform = getPlatform();
  const numericFilters = [
    ...makeNumericFilter({
      price: query.price,
    }),
  ].concat(options.extraNumericFilters.toString().split(',')).filter(Boolean);

  const _searchOptions : SearchOptions = {
    page: initPage,
    distinct: 1,
    facetFilters: makeFacetFilters({
      color: query.color,
      style: query.style,
      category: query.category,
      category2: query.category2,
      room_types: query.room_type,
      published: 'true',
      inStock: 'true',
      ...extraFacetFilters,
    }),
    numericFilters,
    facets: [
      'color',
      'style',
      'category',
      'category2'
    ],
    hitsPerPage,
    analyticsTags: [currentPlatform],
    ...(searchOptions || {}),
  };
  
  return index.search<T>(initQuery, _searchOptions);
}

export const emptyInitResults = { 
  hits: [],
  page: 0,
  nbHits: 0,
  nbPages: 0,
  hitsPerPage: 0,
  processingTimeMS: 0,
  query: '',
  params: '',
  exhaustiveNbHits: false,
  useRouter: true,
};
